import { BrandMark } from '../components/BrandMark';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../api/client';
import type { Role } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { roleLabel } from '../format';
import { LoginForm } from '../components/LoginForm';
import { EnrollmentForm } from '../components/Enrollment';
function AuthCard({ title, lead, children }: { title: string; lead: ReactNode; children: ReactNode }) {
  return (
    <div className="auth-page">
      <div className="card auth-card">
        <div className="brand" style={{ padding: 0 }}>
          <BrandMark />
          mc-panel
        </div>
        <h1>{title}</h1>
        <p className="muted">{lead}</p>
        {children}
      </div>
    </div>
  );
}
function safeNext(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\') ? value : '/';
}
export function Login() {
  const { me, refreshMe } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  if (me?.user) return <Navigate to={next} replace />;
  if (me?.setupRequired)
    return (
      <AuthCard
        title="初期設定が必要です"
        lead="サーバーログのセットアップ URL を開いて最初の管理者を登録してください。"
      >
        <span />
      </AuthCard>
    );
  return (
    <AuthCard title="ログイン" lead="ログイン ID とパスワードを確認してから、認証アプリのコードを入力します。">
      <LoginForm
        devLogin={!!me?.devLogin}
        onDone={async () => {
          await refreshMe();
          navigate(next, { replace: true });
        }}
      />
    </AuthCard>
  );
}
function useCompleted() {
  const { refreshMe } = useApp();
  const navigate = useNavigate();
  return () => {
    void refreshMe().then(() => navigate('/', { replace: true }));
  };
}
export function Setup() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const onDone = useCompleted();
  return (
    <AuthCard
      title="最初の管理者を登録"
      lead={token ? 'パスワードと認証アプリを登録します。' : 'セットアップ URL にトークンが必要です。'}
    >
      {token && (
        <EnrollmentForm
          begin={(input) => api.setupOptions(token, input)}
          finish={(code) => api.setupVerify(token, code)}
          onDone={onDone}
        />
      )}
    </AuthCard>
  );
}
export function AcceptInvite() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<{ role: Role; valid: boolean }>();
  const [error, setError] = useState<string>();
  const onDone = useCompleted();
  useEffect(() => {
    api
      .inviteInfo(token)
      .then(setInfo)
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }, [token]);
  return (
    <AuthCard
      title="mc-panel への招待"
      lead={
        info?.valid
          ? roleLabel[info.role] + 'として参加します。'
          : info || error
            ? '招待が無効か期限切れです。新しいリンクを依頼してください。'
            : '招待を確認しています。'
      }
    >
      {info?.valid && (
        <EnrollmentForm
          begin={(input) => api.inviteOptions(token, input)}
          finish={(code) => api.inviteVerify(token, code)}
          onDone={onDone}
        />
      )}
    </AuthCard>
  );
}
export function ResetCredentials() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<{ login?: string; name: string }>();
  const [error, setError] = useState<string>();
  const onDone = useCompleted();
  useEffect(() => {
    api
      .resetInfo(token)
      .then(setInfo)
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }, [token]);
  return (
    <AuthCard
      title="認証情報を再設定"
      lead={
        error
          ? 'リンクが無効か期限切れです。オーナーに再発行を依頼してください。'
          : '新しいパスワードと認証アプリを登録してください。'
      }
    >
      {info && (
        <EnrollmentForm
          initialLogin={info.login}
          initialName={info.name}
          begin={(input) => api.resetOptions(token, input)}
          finish={(code) => api.resetVerify(token, code)}
          onDone={onDone}
        />
      )}
    </AuthCard>
  );
}
